import type { ReactNode } from 'react';
import { ChevronRight, CircleCheck, CircleDashed, SearchCheck, ShieldAlert, UserRound } from 'lucide-react';
import type { DuplicateCandidate } from '@his/shared';
import { tr } from '@/lib/i18n-core';
import { useReducedMotion } from '@/lib/motion';
import { cn, dateOnly, initials } from '@/lib/utils';
import { Badge, Spinner } from '@/components/ui/primitives';
import { PatientAvatar } from '@/components/PatientAvatar';
import { SECTION_ICON, sectionTitle } from './PatientFormSections';
import { sectionAnchor } from './RegistrationSection';
import type { RegistrationMode, SectionProgress } from './registration-rules';

export const ASIDE_CLASS = 'space-y-4 lg:sticky lg:top-4 lg:-m-1 lg:max-h-[calc(100dvh-6rem)] lg:overflow-y-auto lg:p-1';

const MATCH_LABEL_KEY: Record<string, string> = {
  name: 'match_name',
  phone: 'match_phone',
  cnic: 'match_id_number',
  dob: 'match_date_of_birth',
};

export function matchReasonLabel(reason: string): string {
  const key = MATCH_LABEL_KEY[reason];
  return key ? tr('opd', key) : reason;
}

function Row({ label, value, mono }: { label: string; value: ReactNode; mono?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1.5">
      <dt className="shrink-0 text-xs text-muted-foreground">{label}</dt>
      <dd className={cn('min-w-0 truncate text-end text-sm font-medium', mono && 'font-mono text-[0.8rem] tracking-wide')} dir={mono ? 'ltr' : undefined}>
        {value || <span className="font-normal text-muted-foreground/70">—</span>}
      </dd>
    </div>
  );
}

export function PatientSummaryCard({
  name,
  sex,
  age,
  phone,
  idNumber,
  branch,
  category,
  mrn,
  likelyMrn,
  photoKey,
  photoSrc,
  mode,
  isMinor,
  infant,
  footer,
}: {
  name: string;
  sex: string;
  age: string | null;
  phone: string | null | undefined;
  idNumber: string | null | undefined;
  branch: string | null | undefined;
  category: string | null;
  mrn: string | null;
  likelyMrn?: string | null;
  photoKey?: string | null;
  photoSrc?: string | null;
  mode: RegistrationMode | null;
  isMinor: boolean;
  infant: boolean;
  footer?: ReactNode;
}) {
  const hasName = name.trim().length > 0;
  return (
    <section className="overflow-hidden rounded-xl border bg-card shadow-sm" aria-label={tr('opd', 'summary_card_label')}>
      <div className="relative bg-gradient-to-br from-primary/15 via-primary/5 to-transparent px-4 pb-4 pt-5">
        <div className="flex items-center gap-3">
          <PatientAvatar
            name={name}
            photoKey={photoKey}
            src={photoSrc}
            className={cn(
              'grid h-14 w-14 shrink-0 place-items-center rounded-full text-lg font-semibold shadow-sm ring-4 ring-card transition-colors duration-300',
              hasName ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground',
            )}
            fallback={hasName ? initials(name) : <UserRound className="h-6 w-6" />}
          />
          <div className="min-w-0">
            <p className={cn('truncate text-base font-semibold leading-tight', !hasName && 'text-muted-foreground')}>{hasName ? name : tr('opd', 'new_patient')}</p>
            <p className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
              {mrn ? (
                <span className="font-mono tracking-wide" dir="ltr">
                  {mrn}
                </span>
              ) : likelyMrn ? (
                <span>
                  {tr('opd', 'mrn_likely')}{' '}
                  <span className="font-mono tracking-wide text-foreground/80" dir="ltr">
                    {likelyMrn}
                  </span>
                </span>
              ) : (
                <span>{tr('opd', 'mrn_on_save')}</span>
              )}
            </p>
            {!mrn && likelyMrn && <p className="mt-0.5 text-[0.7rem] leading-snug text-muted-foreground/80">{tr('opd', 'mrn_likely_hint')}</p>}
          </div>
        </div>
        <div className="mt-3 flex flex-wrap gap-1.5">
          {mode === 'quick' && <Badge variant="default">{tr('opd', 'registration_mode_quick')}</Badge>}
          {infant && <Badge variant="info">{tr('opd', 'infant')}</Badge>}
          {isMinor && !infant && <Badge variant="warning">{tr('opd', 'minor')}</Badge>}
          {category && category !== 'general' && <Badge variant="secondary">{category}</Badge>}
        </div>
      </div>
      <dl className="divide-y px-4 py-1">
        <Row label={tr('opd', 'age')} value={age} />
        <Row label={tr('opd', 'sex')} value={sex} />
        <Row label={tr('opd', 'phone')} value={phone} mono />
        <Row label={tr('opd', 'id_number')} value={idNumber} mono />
        {branch !== undefined && <Row label={tr('opd', 'branch')} value={branch} />}
      </dl>
      {footer && <div className="border-t bg-muted/30 px-4 py-3 text-xs text-muted-foreground">{footer}</div>}
    </section>
  );
}

function ProgressRing({ value }: { value: number }) {
  const radius = 20;
  const circumference = 2 * Math.PI * radius;
  const complete = value >= 100;
  return (
    <svg viewBox="0 0 48 48" className="h-12 w-12 -rotate-90" aria-hidden>
      <circle cx="24" cy="24" r={radius} fill="none" strokeWidth="5" className="stroke-muted" />
      <circle
        cx="24"
        cy="24"
        r={radius}
        fill="none"
        strokeWidth="5"
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={circumference * (1 - Math.min(100, Math.max(0, value)) / 100)}
        className={cn('transition-[stroke-dashoffset] duration-500 ease-out', complete ? 'stroke-success' : 'stroke-primary')}
      />
    </svg>
  );
}

export function CompletenessCard({ sections, percent, keepsFullRecords }: { sections: SectionProgress[]; percent: number; keepsFullRecords: boolean }) {
  const reduced = useReducedMotion();
  const jump = (id: SectionProgress['id']) => {
    const target = document.getElementById(sectionAnchor(id));
    if (!target) return;
    target.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
    target.querySelector<HTMLElement>('input:not([type=hidden]), textarea, button[role=radio][tabindex="0"]')?.focus({ preventScroll: true });
  };
  return (
    <section className="rounded-xl border bg-card p-4 shadow-sm" aria-labelledby="registration-completeness-title">
      <div className="flex items-center gap-3">
        <div className="relative grid place-items-center">
          <ProgressRing value={percent} />
          <span className="absolute text-[0.7rem] font-semibold tabular-nums">{percent}%</span>
        </div>
        <div className="min-w-0">
          <h2 id="registration-completeness-title" className="text-sm font-semibold">
            {tr('opd', 'record_completeness')}
          </h2>
          <p className="text-xs text-muted-foreground">
            {percent >= 100 ? tr('opd', 'record_completeness_done') : tr('opd', 'record_completeness_hint')}
          </p>
        </div>
      </div>
      <ul className="mt-3 space-y-0.5">
        {sections.map((section) => {
          const Icon = SECTION_ICON[section.id];
          return (
            <li key={section.id}>
              <button
                type="button"
                onClick={() => jump(section.id)}
                className="group flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-start text-sm transition-colors hover:bg-muted focus-ring"
              >
                <Icon className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                <span className={cn('min-w-0 flex-1 truncate', section.done ? 'text-foreground' : 'text-muted-foreground')}>{sectionTitle(section.id, keepsFullRecords)}</span>
                {section.done ? (
                  <CircleCheck className="h-4 w-4 shrink-0 text-success" aria-label={tr('opd', 'section_complete')} />
                ) : (
                  <CircleDashed className="h-4 w-4 shrink-0 text-muted-foreground/60" aria-label={tr('opd', 'section_incomplete')} />
                )}
                <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground/0 transition-colors group-hover:text-muted-foreground rtl:rotate-180" aria-hidden />
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function scoreTone(score: number): string {
  if (score >= 70) return 'bg-destructive';
  if (score >= 50) return 'bg-warning';
  return 'bg-muted-foreground/50';
}

export function DuplicatesCard({
  id,
  candidates,
  checking,
  checked,
  onOpen,
  acknowledgement,
}: {
  id?: string;
  candidates: DuplicateCandidate[];
  checking: boolean;
  checked: boolean;
  onOpen: (candidate: DuplicateCandidate) => void;
  acknowledgement?: ReactNode;
}) {
  const found = candidates.length > 0;
  return (
    <section
      id={id}
      className={cn('scroll-mt-6 rounded-xl border bg-card shadow-sm transition-colors duration-300', found && 'border-warning/50')}
      aria-labelledby="registration-duplicates-title"
      aria-live="polite"
    >
      <header className="flex items-center gap-2.5 px-4 pt-4">
        <span className={cn('grid h-8 w-8 shrink-0 place-items-center rounded-lg', found ? 'bg-warning/15 text-warning' : 'bg-muted text-muted-foreground')}>
          {found ? <ShieldAlert className="h-4 w-4" aria-hidden /> : <SearchCheck className="h-4 w-4" aria-hidden />}
        </span>
        <h2 id="registration-duplicates-title" className="min-w-0 flex-1 text-sm font-semibold">
          {tr('opd', 'possible_existing_records')}
        </h2>
        {checking ? <Spinner /> : found ? <Badge variant="warning">{candidates.length}</Badge> : null}
      </header>
      {!found && (
        <p className="flex items-start gap-2 px-4 pb-4 pt-2 text-xs text-muted-foreground">
          {checked ? (
            <>
              <CircleCheck className="mt-px h-3.5 w-3.5 shrink-0 text-success" aria-hidden />
              {tr('opd', 'no_matching_records_so_far')}
            </>
          ) : (
            tr('opd', 'duplicates_checked_as_you_type')
          )}
        </p>
      )}
      {found && (
        <>
          <p className="px-4 pt-1 text-xs text-muted-foreground">{tr('opd', 'duplicates_found_hint')}</p>
          <ul className="space-y-2 p-3">
            {candidates.map((candidate) => (
              <li key={candidate.patientId}>
                <button
                  type="button"
                  onClick={() => onOpen(candidate)}
                  className="w-full rounded-lg border bg-background p-3 text-start transition-colors hover:border-warning/50 hover:bg-warning/[0.04] focus-ring motion-safe:animate-in motion-safe:fade-in-0 motion-safe:slide-in-from-top-1"
                >
                  <span className="flex items-start justify-between gap-2">
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-medium">{candidate.fullName}</span>
                      <span className="block text-xs text-muted-foreground" dir="ltr">
                        {candidate.mrn}
                        {candidate.dob ? ` · ${dateOnly(candidate.dob)}` : ''}
                        {candidate.phone ? ` · ${candidate.phone}` : ''}
                      </span>
                    </span>
                    <span className="shrink-0 text-xs font-semibold tabular-nums">{candidate.score}%</span>
                  </span>
                  <span className="mt-2 block h-1 overflow-hidden rounded-full bg-muted">
                    <span className={cn('block h-full rounded-full', scoreTone(candidate.score))} style={{ width: `${candidate.score}%` }} />
                  </span>
                  <span className="mt-2 flex flex-wrap gap-1">
                    {candidate.matchedOn.map((reason) => (
                      <Badge key={reason} variant="muted" className="px-2 py-0 text-[0.7rem]">
                        {matchReasonLabel(reason)}
                      </Badge>
                    ))}
                  </span>
                </button>
              </li>
            ))}
          </ul>
          {acknowledgement && <div className="border-t px-4 py-3">{acknowledgement}</div>}
        </>
      )}
    </section>
  );
}
